import { inject, Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { AuthService } from '../../state/auth/auth.service';
import { environment } from '../../../environments/environment';
import { GuidanceWebSocketMessage } from './guidance-websocket.service.interface';
import { ToastService } from '../../state/toast/toast.service';

@Injectable({
  providedIn: 'root',
})
export class WebSocketService {
  private socket: WebSocket | null = null;
  private messages$ = new Subject<GuidanceWebSocketMessage>();
  private authService = inject(AuthService);
  private toastService = inject(ToastService);
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;

  async connect(): Promise<Observable<GuidanceWebSocketMessage>> {
    if (window.Cypress) {
      console.log('Cypress detected - returning mock websocket');
      return window.cypressWebSocketMock;
    }

    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      return this.messages$.asObservable();
    }

    const token = (await this.authService.getTokens()).accessToken || '';
    this.socket = new WebSocket(`${environment.portal.guidanceWebsocket}/?token=${encodeURIComponent(token)}`);

    this.socket.onopen = () => {
      console.log('WebSocket connection established');
      this.reconnectAttempts = 0;
    };

    this.socket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      this.messages$.next(data);
    };

    this.socket.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    this.socket.onclose = (e) => {
      console.log('WebSocket connection closed', e.code, e.reason);
      this.socket = null;

      if (this.shouldReconnect(e.code)) {
        this.attemptReconnect();
      }
    };

    return this.messages$.asObservable();
  }

  private shouldReconnect(code: number): boolean {
    // Reconnect on:
    // 1001 - Going Away (server restart/maintenance)
    // 1006 - Abnormal closure (network issues)
    // 1011 - Server error
    return [1001, 1006, 1011].includes(code);
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached. Giving up.');
      this.toastService.showToast({
        type: 'error',
        message: $localize`Connection lost. Please refresh the page.`,
        duration: 0,
      });
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), 30000); // Exponential backoff, max 30s

    setTimeout(() => {
      this.connect();
    }, delay);
  }

  sendMessage(message: string): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket not open; message dropped');
    }
  }

  close(): void {
    this.reconnectAttempts = this.maxReconnectAttempts;
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.close(1000, 'Client close');
    }
    this.socket = null;
  }
}
